import { useCallback, useEffect, useRef, useState } from "react";
import { Download, X } from "@/shared/ui/icons";
import { useTranslation } from "react-i18next";
import { useRegisterSW } from "virtual:pwa-register/react";

import { Button, Infobulle } from "@/shared/ui";
import { chunkCharge, chunkDe } from "@/core/pwa/chunks";

/** Toutes les 60 s — la meme cadence que le check du service worker. */
const INTERVALLE_MS = 60_000;

/**
 * Toast bottom-right « Nouvelle version disponible — Recharger ».
 *
 * Dessin (coque, 01-11-2026) : la fenêtre en verre de la librairie
 * (`ov-fenetre`, celle de `Modal`) — le lot « Toasts » du catalogue n'est pas
 * encore là — ; `Button` pour les actions et la croix, plus de capitales.
 *
 * ⛔ Pas de bandeau en haut de la coque : essayé le 02-11, refusé le 03-11 par Kayla (« tout en haut,
 *    ça, c'est nul. Le truc d'avant, à droite, il était bien »). La fenêtre reste en bas à droite.
 *
 * ─── ON PROPOSE, ON NE FORCE PLUS ───────────────────────────────────────────
 *
 * 🧊 Jusqu'au 21-08-2026, un deploiement du cockpit RECHARGEAIT la page de tout
 * le monde, tout seul. Ce n'etait pas ce composant : c'est `vite-plugin-pwa`,
 * dont le mode `autoUpdate` (`vite.config.ts`) fait, a l'activation du nouveau
 * service worker :
 *
 *     wb.addEventListener("activated", (event) => {
 *       if (event.isUpdate || event.isExternal) {
 *         if (onNeedReload) onNeedReload();
 *         else window.location.reload();   // ← notre cas : personne ne fournissait le crochet
 *       }
 *     });
 *
 * Consequence signalee par Kayla : une mise a jour de Twitter, de Herald ou du
 * parcours candidat — des surfaces qu'un chatteur n'ouvre jamais — arrachait
 * quand meme sa page en plein shift, potentiellement au milieu d'un message en
 * cours de redaction. Mesure du 21-08 : sur les 80 derniers deploiements de
 * `main`, 29 (36 %) ne touchaient AUCUN fichier qu'un chatteur execute.
 *
 * On fournit donc `onNeedReload`, et la lib nous rend la decision. Le cockpit
 * ne se recharge plus jamais tout seul : il PROPOSE, et son porteur choisit son
 * moment. Decision de Kayla du 21-08-2026.
 *
 * 🪤 La landing publique n'est PAS un contre-exemple, contrairement a ce que
 * laissait croire le commentaire de `vite.config.ts` (« on active la nouvelle
 * version sans attendre un clic : un candidat ne doit jamais recevoir une
 * landing precedente »). Ce qui protege `/candidature`, c'est
 * `navigateFallbackDenylist` : elle est exclue du fallback du service worker,
 * donc toujours servie par le reseau. Et ce composant n'y est meme pas monte —
 * il vit dans `ChromeLayout`, dont les routes publiques sont SŒURS.
 *
 * 🪤 Verifie le 21-08 avant de toucher au crochet : `dist/registerSW.js`
 * n'existe pas et rien n'est injecte dans `index.html`. Le plugin s'en abstient
 * quand l'app importe elle-meme le module virtuel — sinon il y aurait DEUX
 * enregistrements, et celui d'`index.html` rechargerait la page quoi qu'on
 * fasse ici. Ce `useRegisterSW` est le SEUL, donc le crochet gouverne tout.
 *
 * ⚠️ Ne plus recharger de force a un cout, et il se paie ailleurs : une fenetre
 * qui reste sur un bundle perime finit par demander un chunk que le deploiement
 * a supprime. Le filet est dans `@/core/pwa/chunks`, branche sur
 * l'`ErrorBoundary`. Les deux moities sont le meme correctif — enlever l'une
 * casse l'autre.
 *
 * ─── POURQUOI IL Y A DEUX SIGNAUX, ET PAS UN ────────────────────────────────
 *
 *   ① **Le service worker**, via `onNeedReload`. Il ne se declenche que la ou
 *      un SW tourne — donc jamais dans Tauri.
 *
 *   ② **Le sondage du chunk d'entree**. La webview Tauri n'enregistre AUCUN
 *      service worker (elle charge via un schema custom), donc ① n'y arrive
 *      jamais : la fenetre du bureau resterait sur le bundle du jour ou on l'a
 *      ouverte, indefiniment, sans que rien ne le dise.
 *
 * Les deux convergent vers UN seul etat, `servi` — le nom du chunk d'entree
 * reellement servi. C'est lui qui fait office de numero de version : ecarter le
 * toast vaut pour CETTE version-la seulement. Un `dismissed` booleen aurait
 * masque aussi la suivante, et c'est exactement le silence qu'on corrige ici.
 *
 * 🪤 `?_v=` n'est pas decoratif : sans lui, la requete tombe sur l'entree
 * `index.html` du precache workbox et on comparerait le bundle a lui-meme.
 * `ignoreURLParametersMatching` ne neutralise par defaut que `utm_*` et
 * `fbclid`.
 */
export function PwaUpdateToast() {
  const { t } = useTranslation();
  /** La version ecartee a la main — jamais un booleen, cf. ci-dessus. */
  const [ecartee, setEcartee] = useState<string | null>(null);
  /** Le chunk servi, quand il differe du notre. */
  const [servi, setServi] = useState<string | null>(null);

  const charge = useRef<string | null>(null);
  if (charge.current === null) charge.current = chunkCharge();
  const vivant = useRef(true);

  /**
   * Va voir quel bundle le serveur sert MAINTENANT. Appele au montage, toutes
   * les 60 s, et immediatement quand le service worker signale une activation.
   * C'est ainsi que ① et ② convergent vers le meme etat, avec un vrai nom de
   * version dans les deux cas.
   */
  const sonder = useCallback(async () => {
    const notre = charge.current;
    // Pas de chunk hashe = dev, ou une page servie autrement. On se tait.
    if (notre == null) return;
    try {
      const r = await fetch(`/index.html?_v=${Date.now()}`, {
        cache: "no-store",
      });
      if (!r.ok) return;
      const nom = chunkDe(await r.text());
      // ⛔ Un HTML illisible ne vaut pas « nouvelle version » : hors ligne,
      // portail captif, 502 le temps d'un deploiement — on ne crie pas.
      if (vivant.current && nom != null && nom !== notre) setServi(nom);
    } catch {
      /* hors ligne : ce n'est pas un deploiement */
    }
  }, []);

  useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      // Polling toutes les 60s — le check par défaut (visibility + nav)
      // ne suffit pas : après un hard refresh le SW est frais et les
      // events naturels peuvent rater le prochain deploy.
      setInterval(() => {
        registration.update();
      }, INTERVALLE_MS);
    },
    /**
     * ⛔ LA ligne qui empeche le rechargement force. Sans ce crochet,
     * `vite-plugin-pwa` appelle `window.location.reload()` ici meme, et le
     * chatteur perd ce qu'il etait en train d'ecrire. On se contente d'aller
     * lire quel bundle est servi : le toast s'affichera, et c'est son bouton —
     * donc une personne — qui decidera de recharger.
     */
    onNeedReload() {
      void sonder();
    },
  });

  useEffect(() => {
    vivant.current = true;
    void sonder();
    const id = setInterval(() => void sonder(), INTERVALLE_MS);
    return () => {
      vivant.current = false;
      clearInterval(id);
    };
  }, [sonder]);

  const version = servi;
  if (version == null || version === ecartee) return null;

  function handleDismiss() {
    setEcartee(version);
  }

  return (
    <div className="ov-fenetre ov-arrondi fixed bottom-4 right-4 z-[350] w-80 max-w-[calc(100vw-2rem)] p-4">
      <div className="flex items-start gap-3">
        <Download className="mt-0.5 h-4 w-4 shrink-0 text-muted" />
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-medium leading-snug text-fg">
            {t("shell.pwaUpdate.available")}
          </p>
          <p className="mt-1 text-[13px] leading-relaxed text-muted">
            {t("shell.pwaUpdate.body")}
          </p>
        </div>
        <Infobulle texte={t("shell.pwaUpdate.dismiss")}>
          <Button
            variant="ghost"
            size="md"
            iconOnly
            onClick={handleDismiss}
            aria-label={t("shell.pwaUpdate.dismiss")}
            className="-mr-1.5 -mt-1.5 shrink-0"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </Infobulle>
      </div>
      <div className="mt-4 flex items-center justify-end gap-2">
        <Button variant="secondary" size="lg" onClick={handleDismiss}>
          {t("shell.pwaUpdate.later")}
        </Button>
        <Button
          variant="accent"
          size="lg"
          /* ⛔ `location.reload()`, PAS `updateServiceWorker()` : ce dernier est
             un no-op en mode auto, et en Tauri il n'y a meme pas de SW. Le SW
             fraichement active sert deja le nouveau shell ; sans SW, le reseau
             le sert. Un rechargement suffit dans les deux cas. */
          onClick={() => window.location.reload()}
        >
          {t("shell.pwaUpdate.reload")}
        </Button>
      </div>
    </div>
  );
}
